import { createContext, useContext } from 'react';

import type { TooltipAddSource } from './shortcut-source';

export type CompanySelectionContextValue = {
  /** Is a company (by rcid) already among the current selections? */
  isCompanySelected: (rcid: number) => boolean;
  /** Add (or remove) a company by rcid — resolves its RICS ancestry so it
   * commits as a path-encoded company id, matching a normal tree pick.
   * `source` names the tooltip control that asked, for the add's analytics. */
  toggleCompany: (
    company: { rcid: number; name: string },
    source: TooltipAddSource
  ) => void;
  /** rcid currently being resolved/added, for a pending state; else null. */
  pendingRcid: number | null;
};

const CompanySelectionContext =
  createContext<CompanySelectionContextValue | null>(null);

export const CompanySelectionProvider = CompanySelectionContext.Provider;

/**
 * Read the company-selection add-context. Returns `null` outside a provider
 * (surfaces without an add mechanism), so consumers render no add affordance
 * there — the tooltip "+"/competitors feature is scoped to the company filter.
 */
export const useCompanySelection = (): CompanySelectionContextValue | null =>
  useContext(CompanySelectionContext);

/**
 * Trailing rcid segment of a selection id — handles the plain `k10_k400_rcid`
 * and the placeholder tree's depth-compound `0:k10_1:k400_2:rcid`.
 */
export const getSelectionLeafId = (id: string): string =>
  id.split('_').pop()?.split(':').pop() ?? id;
